import axiosInstance from './axiosConfig';

export type NavGroup = 'my_pages' | 'desk_pages' | 'back_office' | 'other';

export interface NavStatsPageRow {
  path: string;
  navGroup: NavGroup;
  visits: number;
  uniqueUsers: number;
}

export interface NavStatsGroupRow {
  navGroup: NavGroup;
  visits: number;
  uniqueUsers: number;
}

export interface NavStatsRoleRow {
  role: string;
  visits: number;
  uniqueUsers: number;
}

export interface NavStatsRoleGroupRow {
  role: string;
  navGroup: NavGroup;
  visits: number;
  uniqueUsers: number;
}

export interface NavStatsSummary {
  period: { startDate: string; endDate: string };
  byPage: NavStatsPageRow[];
  byNavGroup: NavStatsGroupRow[];
  byRole: NavStatsRoleRow[];
  byRoleAndGroup: NavStatsRoleGroupRow[];
}

export interface NavFlowItem {
  from: string;
  to: string;
  count: number;
}

export interface NavFlowsResponse {
  period: { startDate: string; endDate: string };
  flows: NavFlowItem[];
}

export async function recordPageView(path: string, from?: string): Promise<void> {
  try {
    await axiosInstance.post('/nav-stats/page-view', { path, from });
  } catch {
    // Telemetry must never interrupt navigation.
  }
}

export async function fetchNavStatsSummary(params: {
  startDate?: string;
  endDate?: string;
  role?: string;
  mfaStatus?: 'enabled' | 'disabled';
}): Promise<NavStatsSummary> {
  const search = new URLSearchParams();
  if (params.startDate) search.set('startDate', params.startDate);
  if (params.endDate) search.set('endDate', params.endDate);
  if (params.role) search.set('role', params.role);
  if (params.mfaStatus) search.set('mfaStatus', params.mfaStatus);
  const q = search.toString();
  const { data } = await axiosInstance.get<NavStatsSummary>(`/nav-stats/summary${q ? `?${q}` : ''}`);
  return data;
}

export async function fetchNavFlows(params: {
  startDate?: string;
  endDate?: string;
  role?: string;
}): Promise<NavFlowsResponse> {
  const search = new URLSearchParams();
  if (params.startDate) search.set('startDate', params.startDate);
  if (params.endDate) search.set('endDate', params.endDate);
  if (params.role) search.set('role', params.role);
  const q = search.toString();
  const { data } = await axiosInstance.get<NavFlowsResponse>(`/nav-stats/flows${q ? `?${q}` : ''}`);
  return data;
}

export interface UserNavStatsTopPage {
  path: string;
  visits: number;
}

export interface UserNavStatsRow {
  userId: string;
  totalVisits: number;
  uniquePages: number;
  topPages: UserNavStatsTopPage[];
  avgDailyVisits: number;
  activeDays: number;
  firstActiveDay: string;
  lastActiveDay: string;
  permissionRole?: string;
}

export interface UserNavStatsResponse {
  period: { startDate: string; endDate: string };
  totalCount: number;
  users: UserNavStatsRow[];
}

export interface UserNavDetailPageDay {
  day: string;
  visits: number;
}

export interface UserNavDetailRow {
  path: string;
  navGroup: NavGroup;
  visits: number;
  days: UserNavDetailPageDay[];
}

export interface UserNavDetailResponse {
  userId: string;
  period: { startDate: string; endDate: string };
  pages: UserNavDetailRow[];
  totalVisits: number;
  activeDays: number;
  flowPatterns: Array<{ from: string; to: string; count: number }>;
}

export async function fetchNavStatsByUser(params: {
  startDate?: string;
  endDate?: string;
  role?: string;
  mfaStatus?: 'enabled' | 'disabled';
  limit?: number;
  offset?: number;
}): Promise<UserNavStatsResponse> {
  const search = new URLSearchParams();
  if (params.startDate) search.set('startDate', params.startDate);
  if (params.endDate) search.set('endDate', params.endDate);
  if (params.role) search.set('role', params.role);
  if (params.mfaStatus) search.set('mfaStatus', params.mfaStatus);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.offset !== undefined) search.set('offset', String(params.offset));
  const q = search.toString();
  const { data } = await axiosInstance.get<UserNavStatsResponse>(
    `/nav-stats/by-user${q ? `?${q}` : ''}`
  );
  return data;
}

export async function fetchUserNavDetail(params: {
  userId: string;
  startDate?: string;
  endDate?: string;
}): Promise<UserNavDetailResponse> {
  const search = new URLSearchParams();
  if (params.startDate) search.set('startDate', params.startDate);
  if (params.endDate) search.set('endDate', params.endDate);
  const q = search.toString();
  const { data } = await axiosInstance.get<UserNavDetailResponse>(
    `/nav-stats/user/${encodeURIComponent(params.userId)}${q ? `?${q}` : ''}`
  );
  return data;
}

